/**
 * The free internship's terms and seat limits — the FRONTEND MIRROR of the backend SSOT.
 *
 * WHY THIS EXISTS (2026-09-09). The frontend had no import of these constants
 * at all, so the trial got described three different ways on three surfaces
 * and none of them could be checked. This module is the one place a page reads
 * them from, on the same mirror-plus-gate pattern that
 * src/constants/permissionCatalog.data.json already uses:
 * scripts/check-trial-terms-sync.mjs fails the build if these drift from
 * functions/src/accessCodeRedemption.ts or functions/src/rbac/seatLimit.ts.
 *
 * THE FREE INTERNSHIP (Justin, 2026-10-30; Miles, 2026-11-02: one month, part
 * time). The free credit is an internship: a new organization's primary owner,
 * once per person, gets one month of Prest0n at an intern's rate of
 * INTERNSHIP_WU_PER_MONTH pr0 a month (a part-time employee's month), credited
 * up front (225 pr0). Personal workspaces and people already in an organization
 * get none. Copy names it in pr0 and months, never in dollars: its words are
 * src/config/internshipWords.ts, built from these figures.
 *
 * The live values: ops/trialTiers can change the code tiers (a, b) without a
 * deploy, never below the free internship; the free internship itself (organic)
 * is fixed in code, because the Terms of Service publish it
 * (functions/src/accessCodeRedemption.ts resolveTrialTiers). Anything that must
 * reflect a specific account's real standing reads the account, not this file.
 */
import { STANDARD_RATE_USD_PER_WU, formatWu } from './workUnits';

/** One tier's terms: what the wallet is credited and how long the clock runs. */
export interface TrialTierTerms {
  creditAmountCents: number;
  durationDays: number;
}

/**
 * Mirrors DEFAULT_TRIAL_TIERS in functions/src/accessCodeRedemption.ts. Every internship runs one month.
 *   a       PREST0-… invitation codes: an extended internship of $1,000 of pr0
 *   b       EVAL-… invitation codes: an extended internship, 420 pr0
 *   organic the free internship, at the intern's rate, for a new organization's primary owner: 225 pr0
 */
export const TRIAL_TIERS = {
  a: { creditAmountCents: 100000, durationDays: 30 },
  b: { creditAmountCents: 91560, durationDays: 30 },
  organic: { creditAmountCents: 49050, durationDays: 30 },
} as const satisfies Record<'a' | 'b' | 'organic', TrialTierTerms>;

/** An intern's rate: the pr0 a month the free internship is worked out at. Mirrors the server's. */
export const INTERNSHIP_WU_PER_MONTH = 225;

/** The free internship: what a new organization's primary owner gets, with no code and no card. */
export const INTERNSHIP = TRIAL_TIERS.organic;

/** Days on the free internship's clock: 30, one month. */
export const INTERNSHIP_DAYS = INTERNSHIP.durationDays;

/** The pr0 a grant carries, read back from its cents at the Standard Rate, to one decimal: 225 for the internship. */
export function termsWu(terms: TrialTierTerms = INTERNSHIP): number {
  return Math.round((terms.creditAmountCents / 100 / STANDARD_RATE_USD_PER_WU) * 10) / 10;
}

/** The free internship's pr0: 225. */
export const INTERNSHIP_WU = termsWu(INTERNSHIP);

/** "225 pr0": a grant's pr0, for copy. */
export function formatTermsWu(terms: TrialTierTerms = INTERNSHIP): string {
  return `${formatWu(termsWu(terms))} pr0`;
}

/** Days in the month every allocation counts (a 30-day month): an internship of 30 days is "1 month". */
const MONTH_DAYS = 30;

/** Whole months when the days divide by thirty (1 for the internship), else null. */
export function termsMonths(terms: TrialTierTerms = INTERNSHIP): number | null {
  return terms.durationDays % MONTH_DAYS === 0 ? terms.durationDays / MONTH_DAYS : null;
}

/** Whole weeks when the days divide by seven (and are not whole months), else null. */
export function termsWeeks(terms: TrialTierTerms = INTERNSHIP): number | null {
  return terms.durationDays % 7 === 0 ? terms.durationDays / 7 : null;
}

/** "1 month", "2 months", "3 weeks", or "10 days" when the days are neither. */
export function formatTermsLength(terms: TrialTierTerms = INTERNSHIP): string {
  const months = termsMonths(terms);
  if (months !== null) return `${months} ${months === 1 ? 'month' : 'months'}`;
  const weeks = termsWeeks(terms);
  if (weeks !== null) return `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
  return `${terms.durationDays} ${terms.durationDays === 1 ? 'day' : 'days'}`;
}

/** "1-month", "3-week" or "10-day": the length as an adjective ("a free 1-month internship"). */
export function formatTermsLengthAdjective(terms: TrialTierTerms = INTERNSHIP): string {
  const months = termsMonths(terms);
  if (months !== null) return `${months}-month`;
  const weeks = termsWeeks(terms);
  return weeks !== null ? `${weeks}-week` : `${terms.durationDays}-day`;
}

/** Seats an organization may ADD during the trial promo, beyond the owner. */
export const TRIAL_ADDED_SEATS = 5;

/** Owner seat plus the trial invitees — the total, counting pending invites. */
export const TRIAL_MAX_TEAM_SIZE = 1 + TRIAL_ADDED_SEATS;

/**
 * An armed internship (a card on file and a plan chosen) holds this many people, counting pending invites; null is
 * unlimited, as it is since 2026-11-03 ("unlimited seats" during the internship too). Mirrors
 * functions/src/rbac/seatLimit.ts.
 */
export const ARMED_EVALUATION_MAX_TEAM_SIZE: number | null = null;

/*
 * There is no paid-lapse grace (2026-10-11, Miles): a plan payment that fails pauses new work at once —
 * src/config/billingPause.ts.
 */
